"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

const leadershipServices = [
  {
    title: "Fractional CTO / CIO / CISO",
    description: "Executive technology leadership without the full-time overhead. JAMO steps into your organization and leads from day one—bridging the gap between your technical teams and the boardroom.",
  },
  {
    title: "Team Building & Operations",
    description: "Engineering teams, SRE organizations, and security departments built from scratch. We know what it takes to hire, structure, and retain at scale—because we\u2019ve done it across organizations of hundreds.",
  },
  {
    title: "Incident Management & Process",
    description: "Incident management processes, post-incident reviews, and operational procedures designed for real-world scale. From building the program to running it, JAMO delivers systems that hold up under pressure.",
  },
  {
    title: "Vendor & Budget Strategy",
    description: "Metrics-driven prioritization of technology spend, vendor negotiations, and executive reporting. We deliver CEO-level briefings on project prioritization backed by real data—not slide decks.",
  },
];

const technicalServices = [
  {
    title: "Cloud Architecture & Migration",
    description: "Cloud environments redesigned at scale, datacenter-to-cloud migrations, and hybrid architectures that actually work in production. JAMO brings deep hands-on AWS expertise to every engagement.",
  },
  {
    title: "DevOps & Automation",
    description: "CI/CD pipeline design, infrastructure as code, and enabling true DevOps culture so your teams can ship fast and safely. We bring enterprise-level experience owning the entire pipeline and all its components.",
  },
  {
    title: "Security & Zero Trust",
    description: "From expanding blue teams to designing zero-trust environments—comprehensive security programs and operations including physical security modernization and dedicated security TPM teams.",
  },
  {
    title: "Monitoring & Reliability",
    description: "Centralized monitoring with event correlation and anomaly detection. SRE teams, MCC teams, and monitoring systems supporting thousands of servers with zero-downtime requirements—designed, built, and handed off.",
  },
];

export default function ServicesSection() {
  const [activeTab, setActiveTab] = useState<"leadership" | "technical">("leadership");

  return (
    <section id="services" aria-label="Services" className="py-20 sm:py-24 md:py-32 bg-[#0C0C0E] relative">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-end mb-12 sm:mb-16 md:mb-20">
          <div>
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 reveal-hidden" data-reveal>
              <div className="w-6 sm:w-8 h-[1px] bg-[#B8986A]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#B8986A]">
                Our Services
              </span>
            </div>
            <h2
              className="font-fraunces text-[clamp(2rem,8vw,4.5rem)] sm:text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.05] tracking-tight text-[#F2EFE9] reveal-hidden"
              data-reveal
            >
              Hands-On Technology
              <br />
              <span className="italic text-gradient-gold">Leadership.</span>
            </h2>
          </div>
          <div className="reveal-hidden delay-200" data-reveal>
            <p className="text-[#A09D97] text-base sm:text-lg font-light leading-relaxed">
              Whether you need executive technology leadership or deep infrastructure expertise, JAMO delivers hands-on capability across the full stack—from cloud architecture and DevOps to security and team building.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 mb-12 sm:mb-16 p-1 bg-[#111114] rounded-sm border border-[rgba(242,239,233,0.06)] w-full sm:w-fit reveal-hidden" data-reveal role="tablist" aria-label="Service categories">
          <button
            role="tab"
            aria-selected={activeTab === "leadership"}
            aria-controls="services-panel"
            onClick={() => setActiveTab("leadership")}
            className={`flex-1 sm:flex-none px-6 sm:px-8 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold tracking-[0.15em] uppercase transition-all duration-300 rounded-sm ${
              activeTab === "leadership" ?"bg-[#B8986A] text-[#0C0C0E]" :"text-[#6B6864] hover:text-[#A09D97]"
            }`}
          >
            Leadership & Management
          </button>
          <button
            role="tab"
            aria-selected={activeTab === "technical"}
            aria-controls="services-panel"
            onClick={() => setActiveTab("technical")}
            className={`flex-1 sm:flex-none px-6 sm:px-8 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold tracking-[0.15em] uppercase transition-all duration-300 rounded-sm ${
              activeTab === "technical" ?"bg-[#B8986A] text-[#0C0C0E]" :"text-[#6B6864] hover:text-[#A09D97]"
            }`}
          >
            Technical Expertise
          </button>
        </div>

        {/* Service Cards */}
        <div id="services-panel" role="tabpanel" aria-label={activeTab === "leadership" ? "Leadership & Management services" : "Technical Expertise services"} className="grid sm:grid-cols-2 gap-px bg-[rgba(242,239,233,0.06)]">
          {(activeTab === "leadership" ? leadershipServices : technicalServices)?.map(
            (service, i) => (
              <div
                key={`${activeTab}-${service?.title}`}
                className="service-card bg-[#0C0C0E] p-6 sm:p-8 md:p-10"
              >
                {/* Number */}
                <div className="text-[10px] font-bold tracking-[0.3em] text-[#B8986A] mb-4 sm:mb-6 font-manrope">
                  {String(i + 1)?.padStart(2, "0")}
                </div>

                {/* Title */}
                <h3 className="font-fraunces text-xl sm:text-2xl font-light text-[#F2EFE9] mb-3 sm:mb-4 tracking-tight">
                  {service?.title}
                </h3>

                {/* Description */}
                <p className="text-[#6B6864] text-sm font-light leading-relaxed mb-6 sm:mb-8">
                  {service?.description}
                </p>

                {/* CTA */}
                <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-[#B8986A] group cursor-pointer">
                  <span>Learn More</span>
                  <Icon
                    name="ArrowRightIcon"
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </div>
              </div>
            )
          )}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 sm:mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 sm:gap-8 pt-12 sm:pt-16 border-t border-[rgba(242,239,233,0.06)] reveal-hidden" data-reveal>
          <div>
            <h3 className="font-fraunces text-2xl sm:text-3xl font-light text-[#F2EFE9] mb-2">
              Not sure which service fits?
            </h3>
            <p className="text-[#6B6864] text-sm font-light">
              Every engagement is tailored to your specific needs. Let&apos;s talk about what you&apos;re trying to accomplish.
            </p>
          </div>
          <a href="#contact" className="btn-primary whitespace-nowrap w-full md:w-auto text-center">
            <span>Book a Discovery Call</span>
          </a>
        </div>
      </div>
    </section>
  );
}